import { useState, type ReactNode } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import {
  DeltaInterval,
  DeltaIntervalType,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

export const COUNTING_LABEL: Record<DeltaIntervalType, string> = {
  [DeltaIntervalType.Calendar]: 'Calendar days',
  [DeltaIntervalType.Working]: 'Business days',
  [DeltaIntervalType.Clear]: 'Clear days',
  [DeltaIntervalType.Court]: 'Court days',
};

/**
 * Title-line stand-in for the date when an event or suggestion has no start
 * date yet.
 */
export function DateTbdLabel() {
  return <span className="shrink-0 text-gray-400">TBD</span>;
}

/**
 * Shown on suggestion rows and on accepted events whose date is flagged as not
 * yet pinned down (e.g. depends on an earlier unresolved decision).
 */
export function UnconfirmedChip({ onClick }: { onClick?: (e: React.MouseEvent) => void }) {
  const icon = <Icon className="lucide--circle-dashed" />;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {onClick ? (
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className="shrink-0 p-0 text-amber-500 hover:bg-transparent hover:text-amber-600"
            onClick={onClick}
            aria-label="Review this unconfirmed event"
          >
            {icon}
          </Button>
        ) : (
          <span tabIndex={0} className="inline-flex shrink-0 text-amber-500">
            {icon}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-wrap">
        This event is not yet confirmed and may change as earlier decisions in the timeline are resolved.
        {onClick ? ' Click to review suggestions.' : ''}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Truncating source-document link shown in a row's top-right action group.
 * The click handler decides what opens (the source drawer with the row's
 * chunk reference).
 */
export function SourceFileButton({ fileName, onClick }: { fileName: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          onClick={onClick}
          className="max-w-48 min-w-0 justify-start gap-1.5 truncate p-0 text-xs font-normal text-blue-600 hover:bg-transparent hover:text-blue-600 hover:underline"
        >
          <Icon className="lucide--file-text shrink-0" />
          <span className="truncate">{fileName}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{fileName}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Collapsible details footer shared by event and suggestion rows: a "Details"
 * chevron toggle over an auto-arranging grid of {@link DetailCell}s, animated
 * open/closed. Owns its own open state. An optional `leading` node sits on the
 * same line as the toggle (e.g. the row's description or reasoning), so the
 * toggle is in line with that text rather than below it. With no `leading`, the
 * toggle is right-aligned as before.
 */
export function DetailsAccordion({
  children,
  className,
  leading,
}: {
  children: ReactNode;
  className?: string;
  leading?: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={className}>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0 flex-1">{leading}</div>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          className="shrink-0 gap-1 p-0 text-xs font-normal text-gray-400 hover:bg-transparent hover:text-gray-700"
        >
          Details
          <Icon className={cn('lucide--chevron-down transition-transform', isOpen && 'rotate-180')} />
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <dl className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-x-6 gap-y-3 pt-3 pb-0.5 text-xs">
              {children}
            </dl>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** One label-over-value cell inside a {@link DetailsAccordion}. */
export function DetailCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-light text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-gray-600">{children}</dd>
    </div>
  );
}

const COUNTING_INLINE: Record<DeltaIntervalType, string> = {
  [DeltaIntervalType.Calendar]: 'calendar',
  [DeltaIntervalType.Working]: 'business',
  [DeltaIntervalType.Clear]: 'clear',
  [DeltaIntervalType.Court]: 'court',
};

interface RelationshipUnit {
  value: number;
  singular: string;
  plural: string;
}

/**
 * Render the anchor relationship as `"Occurs 14 business days after Defence
 * due"` (or `"… before"`, or `"the same time as"` for a zero interval).
 * Returns null if we can't resolve the anchor name or there's no interval.
 */
export function formatRelationship(
  interval: DeltaInterval | null,
  type: DeltaIntervalType | null,
  anchorName: string | null,
): ReactNode | null {
  if (anchorName === null) return null;
  if (interval === null) return `Occurs at the same time as ${anchorName}`;

  const units: RelationshipUnit[] = [
    { value: interval.years ?? 0, singular: 'year', plural: 'years' },
    { value: interval.months ?? 0, singular: 'month', plural: 'months' },
    { value: interval.days ?? 0, singular: 'day', plural: 'days' },
    { value: interval.hours ?? 0, singular: 'hour', plural: 'hours' },
    { value: interval.minutes ?? 0, singular: 'minute', plural: 'minutes' },
  ];

  const nonZero = units.filter((u) => u.value !== 0);
  if (nonZero.length === 0) return `Occurs at the same time as ${anchorName}`;

  // Practical event-data assumption: all non-zero components share a sign
  // (an interval like "1 month minus 5 days" is theoretically expressible but
  // not produced by the extractor). Pick the first non-zero unit's sign.
  const direction = nonZero[0].value < 0 ? 'before' : 'after';
  const countingLabel = type !== null ? COUNTING_INLINE[type] : null;

  const segments: ReactNode[] = ['Occurs '];
  nonZero.forEach((u, idx) => {
    const abs = Math.abs(u.value);
    const word = abs === 1 ? u.singular : u.plural;
    const prefix = idx > 0 ? ', ' : '';
    if (u.singular === 'day' && countingLabel !== null) {
      segments.push(`${prefix}${abs} `);
      segments.push(
        <strong key={`b${idx}`} className="font-semibold">
          {countingLabel}
        </strong>,
      );
      segments.push(` ${word}`);
    } else {
      segments.push(`${prefix}${abs} ${word}`);
    }
  });
  segments.push(` ${direction} ${anchorName}`);

  return <span>{segments}</span>;
}
